/**
 * Brand tokens, in one place, applied as CSS custom properties.
 *
 * `guards/scan.ts`'s raw-hex guard only scans `.ts`/`.tsx`/`.js`/`.jsx`/
 * `.mjs`/`.json` — it does not scan `.css` (see its `HEX_GLOBS`). That is
 * a real gap for a package whose static assets are plain CSS, but this
 * file closes it by construction rather than by exception: every literal
 * color value in this package lives in `@zov/core`'s `tokens.ts` (the
 * one file the guard DOES pin as the sole source of raw hex) and reaches
 * `public/styles.css` only as a `var(--zov-*)` custom property set here,
 * in TypeScript, from `color`. `styles.css` itself never spells a hex
 * digit — confirmed true again after Bianca's gate on PR #83 caught an
 * EARLIER version of this file's own claim going stale: `styles.css` had
 * picked up six hand-copied hex literals as `var(--zov-*, #hex)`
 * fallback values, which is exactly the guard-blind second copy this
 * comment says doesn't exist. Fixed by removing the fallbacks entirely
 * (see `styles.css`'s own comment for why that's safe) rather than by
 * updating them to match — a fallback that has to be kept in sync by
 * hand is the drift risk, not a one-time typo. If a future change adds a
 * *second* place that sets colors on `:root` outside this function, or
 * reintroduces a hex fallback in `styles.css`, that reopens the gap this
 * comment describes.
 */

import { color, motion } from '@zov/core';

export function applyTheme(root: HTMLElement): void {
  root.style.setProperty('--zov-ground', color.ground);
  root.style.setProperty('--zov-identity', color.identity);
  root.style.setProperty('--zov-ink', color.ink);
  root.style.setProperty('--zov-scoop-0', color.scoops[0]);
  root.style.setProperty('--zov-scoop-1', color.scoops[1]);
  root.style.setProperty('--zov-scoop-2', color.scoops[2]);
  root.style.setProperty('--zov-ease-glide-in', cubicBezier(motion.glideIn));
  root.style.setProperty('--zov-ease-glide-out', cubicBezier(motion.glideOut));
  root.style.setProperty('--zov-ease-turn', cubicBezier(motion.turn));
}

function cubicBezier(
  points: readonly [number, number, number, number],
): string {
  return `cubic-bezier(${points.join(', ')})`;
}
